/**
 * FreeCreate 表单状态 hook —— 抽自 FreeCreatePage 的全部 useState。
 *
 * 仅承载状态 + setter,所有提交/草稿/派生逻辑都在另外两个 hook 里。
 */
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  AnchorKey9,
  CoverageChip,
  FilterConfig,
  Provider,
  ResolutionTier,
} from "../../api/posterService";
import { FONT_STYLE_AUTO } from "../../ui/components/free-create/fontStyleOptions";
import { findStyleByTone } from "../../ui/components/free-create/styleOptions";
import type { StyleTokens } from "../../longimage/model/canvas";
import { toneToTokens } from "../style/toneToTokens";
import type { GroupPointForm, PosterExplosiveHandoff } from "../blueprint";
import type { AbsorbedMaterials } from "./buildRawPrompt";
import type { FreeCreateProduct } from "./productAdapter";
import type { MultiRefImage } from "./multiRefImages";

// V7 (2026-05-12) — 5 个 ratio 全开,跟 backend SizePreset enum 完全对齐。
export type Ratio = "1:1" | "4:5" | "3:4" | "9:16" | "16:9";
export type Count = 1 | 2 | 3 | 4;
export type ScheduleMode = "now" | "later";
export type CreateMode = "T2I" | "I2I";

/** i2i 参考图(用户上传) — 复用 /poster/uploads 返回的 objectKey/tempUrl + 本地预览。 */
export interface RefImage {
  objectKey: string;
  tempUrl: string;
  previewUrl: string;
}

/** 2026-05-25 — 「明亮鲜艳」预设(保留备用,详情页可一键套用)。 */
export const DEFAULT_VIBRANT_FILTER: FilterConfig = {
  brightness: 10,
  contrast: 10,
  saturation: 20,
  warmth: 0,
};

/** 2026-06-01 — 自由创作默认改为中性(不做后处理调色,保留模型原始色调)。 */
export const NEUTRAL_FILTER: FilterConfig = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
};

export interface FreeCreateForm {
  // ───── 基础描述
  prompt: string;
  setPrompt: (v: string) => void;
  /** #1 (2026-07-04) 特别要求(选填):透传 creativeOverrides.extraConstraint。 */
  extraConstraint: string;
  setExtraConstraint: (v: string) => void;
  ratio: Ratio;
  setRatio: (v: Ratio) => void;
  /** 用户是否在参数弹窗手动改过比例(true 时 AI 优化不覆盖回填)。 */
  ratioUserTouched: boolean;
  setRatioUserTouched: (v: boolean) => void;
  /** P-B 多比例套图:非 null = 套图模式(每比例 1 张);null = 单比例。 */
  ratioSet: Ratio[] | null;
  setRatioSet: (v: Ratio[] | null) => void;
  /** P-B 组图按爆点分张:非 null = 组图模式(张数=爆点数,≤6)。 */
  groupPoints: GroupPointForm[] | null;
  /** W6:主体一致性锚定(组图/套图)。默认开 —— 多张的用户预期本来就是「同一个东西的不同张」。 */
  keepSubjectConsistent: boolean;
  /** 组图产品锚(2026-08-03):产品实拍 adopt 后的 uploads objectKey;null=没锚(没选产品/没实拍/用户关了)。 */
  groupAnchorRef: string | null;
  /** 组图产品锚开关。默认开 —— 选了产品还让引擎乱画产品,违背选产品的意图;关的口留给「只要氛围不要实物」。 */
  groupAnchorOn: boolean;
  /** 组图主体锚句(AI 排台本产出;null=没有)。随建任务上送,ai 逐张注入保主体一致。 */
  groupSubjectSpec: string | null;
  /** B6 场景打标(场景卡进来时记下,提交随请求上送;直接进页=null 不打标)。 */
  sceneKey: string | null;
  setGroupPoints: (v: GroupPointForm[] | null) => void;
  setGroupAnchorRef: (v: string | null) => void;
  setGroupAnchorOn: (v: boolean) => void;
  setGroupSubjectSpec: (v: string | null) => void;
  setKeepSubjectConsistent: (v: boolean) => void;
  setSceneKey: (v: string | null) => void;
  /** P-B 蓝图预载(从爆款情报路由来);null = 无蓝图。 */
  explosive: PosterExplosiveHandoff | null;
  setExplosive: (v: PosterExplosiveHandoff | null) => void;
  /** 创建模式:文字生成 / 图改图。 */
  mode: CreateMode;
  setMode: (v: CreateMode) => void;
  /** i2i 参考图(mode=I2I 时必填)。 */
  refImage: RefImage | null;
  /** R5 局部重绘:涂出来的 mask(与原图同尺寸黑白 PNG;白=要改)。null = 整图重绘。 */
  /** 改图态:用户涂没涂圈选区域(mask 本体在画笔组件里,提交时才导出上传)。 */
  maskPainted: boolean;
  setMaskPainted: (v: boolean) => void;
  setRefImage: (v: RefImage | null) => void;
  /** W2-B9 多图参考(可选,≤4 张,每张带用途);空 = 不发 refImageRefs。 */
  refImages: MultiRefImage[];
  setRefImages: (v: MultiRefImage[]) => void;

  // ───── 品牌资产
  logoId: string | null;
  setLogoId: (v: string | null) => void;
  qrId: string | null;
  setQrId: (v: string | null) => void;
  useBrandColors: boolean;
  setUseBrandColors: (v: boolean) => void;
  /** 任务 14:logo / 二维码的九宫格锚点。null = 自动(按画面留白挑角),是一等选项不是空值。 */
  logoAnchor: AnchorKey9 | null;
  setLogoAnchor: (v: AnchorKey9 | null) => void;
  qrAnchor: AnchorKey9 | null;
  setQrAnchor: (v: AnchorKey9 | null) => void;
  printContact: boolean;
  setPrintContact: (v: boolean) => void;
  useGptThinking: boolean;
  setUseGptThinking: (v: boolean) => void;

  // ───── 出图参数
  count: Count;
  setCount: (v: Count) => void;
  provider: Provider;
  setProvider: (v: Provider) => void;
  resolutionTier: ResolutionTier | null;
  setResolutionTier: (v: ResolutionTier | null) => void;
  scheduleMode: ScheduleMode;
  setScheduleMode: (v: ScheduleMode) => void;
  scheduledAt: string | null;
  setScheduledAt: (v: string | null) => void;
  filter: FilterConfig;
  setFilter: (v: FilterConfig) => void;
  filterMode: "simple" | "advanced";
  setFilterMode: (v: "simple" | "advanced") => void;

  // ───── 提交 / 润色
  polishing: boolean;
  setPolishing: (v: boolean) => void;
  submitting: boolean;
  setSubmitting: (v: boolean) => void;

  // ───── 草稿
  currentDraftId: number | null;
  setCurrentDraftId: (v: number | null) => void;
  draftIdParam: string | null;

  // ───── 2026-05-18 — 风格选择(StylePanel)
  /** 用户显式选定的风格调性。null = 自由发挥(Qwen 据行业气质判断) */
  selectedStyle: string | null;
  setSelectedStyle: (v: string | null) => void;
  /** #20·28 (2026-07-04) 字体风格档 key(fontStyleOptions)。默认 FONT_STYLE_AUTO = 不注入。 */
  fontStyle: string;
  setFontStyle: (v: string) => void;

  // ───── V0 (2026-07-25) L2「配色」(定案 3.2.2;长图侧同款语义)
  /** L2 配色;null = 不指定,由引擎自判。单帧路径**只作提示词注入**(事实③)。 */
  styleTokens: StyleTokens | null;
  /** 选一份配色 —— 任一改动都熄灭「跟随画风」。 */
  setStyleTokens: (v: StyleTokens | null) => void;
  /** 「跟随画风」态:纯前端,不进 payload。 */
  followStyle: boolean;
  /** 复位成跟随画风(按当前画风重推)。 */
  resetFollowStyle: () => void;

  // ───── 波B (2026-07-26) 产品库直选
  /**
   * 从产品库选的那一份快照(逐字原文);null = 没选。
   * 提交时拼成【产品】段进 `rawPrompt`(单帧是 FREEFORM_RAW,结构化产品字段在这条路上
   * 是占位的,见 `productAdapter.ts` 文件头)。
   */
  product: FreeCreateProduct | null;
  setProduct: (v: FreeCreateProduct | null) => void;
  /** 「在图上显示产品参数」(2026-10-19)。默认关 —— 参数只作背景资料,不画进图里。 */
  showProductSpecs: boolean;
  setShowProductSpecs: (v: boolean) => void;

  // ───── prompt-assembly W1 (2026-07-29) 归整层吸收快照
  /**
   * 点 AI 优化那一刻,归整层**吸收了哪一份素材**。null = 没优化过 / 优化失败 /
   * 还原了原文 —— 一律当作没吸收,提交时照旧追加(宁可重复也不丢)。
   */
  absorbed: AbsorbedMaterials | null;
  setAbsorbed: (v: AbsorbedMaterials | null) => void;

  // ───── Wave 3 (2026-05-17) AI 优化产物 state
  /** 用户点 AI 优化前的原 textarea 内容,用于"恢复原文"。null=未优化过。 */
  originalPromptSnapshot: string | null;
  setOriginalPromptSnapshot: (v: string | null) => void;
  /** AI 优化后的内容描述段(用户可改,提交时拼装)。 */
  contentDescription: string;
  setContentDescription: (v: string) => void;
  /** AI 优化后的系统约束段(只读,提交时拼装)。 */
  systemConstraints: string;
  setSystemConstraints: (v: string) => void;
  /** Qwen 识别到的信息点 chip(≤10 个)。 */
  coverageChips: CoverageChip[];
  setCoverageChips: (v: CoverageChip[]) => void;
  /** 当前 textarea 是否为 AI 优化产物;true → 提交时加 [AI-OPTIMIZED] sentinel。 */
  isAiOptimized: boolean;
  setIsAiOptimized: (v: boolean) => void;
  /** Qwen 调用失败的提示文案;非 null → AI 优化按钮 disabled + 显示提示。 */
  optimizeFailureMessage: string | null;
  setOptimizeFailureMessage: (v: string | null) => void;
}

export function useFreeCreateForm(): FreeCreateForm {
  // ───────── 表单状态 ─────────
  const [prompt, setPrompt] = useState("");
  const [extraConstraint, setExtraConstraint] = useState("");
  const [ratio, setRatio] = useState<Ratio>("1:1");
  const [ratioUserTouched, setRatioUserTouched] = useState(false);
  const [ratioSet, setRatioSet] = useState<Ratio[] | null>(null);
  const [groupPoints, setGroupPoints] = useState<GroupPointForm[] | null>(null);
  const [keepSubjectConsistent, setKeepSubjectConsistent] = useState(true);
  const [groupAnchorRef, setGroupAnchorRef] = useState<string | null>(null);
  const [groupAnchorOn, setGroupAnchorOn] = useState(true);
  const [groupSubjectSpec, setGroupSubjectSpec] = useState<string | null>(null);
  const [sceneKey, setSceneKey] = useState<string | null>(null);
  const [explosive, setExplosive] = useState<PosterExplosiveHandoff | null>(null);
  const [mode, setMode] = useState<CreateMode>("T2I");
  const [refImage, setRefImage] = useState<RefImage | null>(null);
  const [maskPainted, setMaskPainted] = useState(false);
  const [refImages, setRefImages] = useState<MultiRefImage[]>([]);

  // 资产切换:默认「本次不使用」(2026-05-18 口径)。
  // ★1007 用户反馈「生图默认不需要自动勾选 logo 和二维码」:撤回 1005 的「品牌库有素材即默认叠上」
  //   (applyBrandDefaults),回到默认不叠、用户要就点选;品牌资产页文案同批改成「出图时可选叠加」。
  const [logoId, setLogoId] = useState<string | null>(null);
  const [qrId, setQrId] = useState<string | null>(null);
  // V11.6 (2026-05-12) — 本次是否启用品牌色
  // 2026-05-18: 跟 logo/QR 一致默认关,跟"默认不使用品牌资产"语义对齐。
  const [useBrandColors, setUseBrandColors] = useState(false);
  const [logoAnchor, setLogoAnchor] = useState<AnchorKey9 | null>(null);
  const [qrAnchor, setQrAnchor] = useState<AnchorKey9 | null>(null);
  /**
   * 2026-07-30 — 本次是否把品牌资料里的电话 / 邮箱印在图上。
   *
   * ★默认 **false**,与 logo / QR / 品牌色同一条「默认不使用品牌资产」的口径。
   *   改之前这两个字段是条死通道(前端发了、ai 收了、算成 `contact_lines` 之后没有
   *   任何消费方,出图指令里一个字都没有),接通那一刻若默认开,等于给所有已经填过
   *   电话的存量租户的海报无声地多印一行 —— 那是他们没要过的变更。
   */
  const [printContact, setPrintContact] = useState(false);
  // v1.4 (2026-05-14): FreeCreate「说自由就自由」— 默认 raw prompt 直送,industry hint
  // 不出现在 FreeCreate UI(ai-service 端 FREEFORM_RAW 强制跳过 wrapper / hint)。
  // GPT thinking 默认 off,用户主动 opt-in。
  const [useGptThinking, setUseGptThinking] = useState(false);

  // V26 Wave 5 — 删高级折叠,全部展平到主区。advancedSummary 同时删。
  // V25 (2026-05-10) — default 4→1,4 张应用场景少且费 4× quota,需要时用户手动加
  const [count, setCount] = useState<Count>(1);
  // 2026-05-25 — 默认 GPT(OPENAI):质感最好,作为低技能用户的「默认即好」。
  // OPENAI 在 capabilities 里 enabled,constraints 仅在 provider 被 disabled 时才强切,
  // 所以这个默认会保留;用户可在 ⚙参数→高级 切换。
  const [provider, setProvider] = useState<Provider>("OPENAI");
  // V7 (2026-05-12) — 分辨率档位。null = 走 provider defaultResolutionTier(下面 useEffect
  // 在 capabilities 到货 / 切 provider 时自动校正,不让用户卡在"无选择"状态)。
  const [resolutionTier, setResolutionTier] = useState<ResolutionTier | null>(null);
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>("now");
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  // 2026-06-01 — 出图色彩默认中性(不做后处理调色,保留模型原始色调)。
  // 之前默认「明亮鲜艳」会把所有图统一拉亮加饱和,现按用户要求改回默认;详情页仍可手动调。
  const [filter, setFilter] = useState<FilterConfig>({ ...NEUTRAL_FILTER });
  const [filterMode, setFilterMode] = useState<"simple" | "advanced">("simple");

  // submit / polish
  const [polishing, setPolishing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // V26 (2026-05-14) Wave 4.4 — 草稿入口:URL ?draftId= 恢复 + "保存草稿" button
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get("draftId");
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(
    draftIdParam ? Number(draftIdParam) : null
  );

  // 2026-05-18 — 风格选择
  const [selectedStyle, setSelectedStyleState] = useState<string | null>(null);
  // #20·28 (2026-07-04) — 字体风格档(默认 auto,不注入)
  const [fontStyle, setFontStyle] = useState<string>(FONT_STYLE_AUTO);

  /**
   * ═══ V0 L2「配色」state(定案 3.2.2/§4.3)══════════════════════════════════════
   * 单帧此前完全没有 L2:用户在长图里能定的「配色」,到了单帧只剩一个画风,
   * 同一个舱两条路径能定的东西不一样。补上之后两条路同构。
   *
   * ★这条路径上配色**只作提示词注入**(已核准事实③):单帧海报整张由引擎画、
   * 没有 PIL 矢量块,`CreativeOverridesPayload` 也没有 styleTokens 通道 ——
   * 渲成一句色板描述拼进【风格】段就是正确实现,不为它改 payload schema。
   *
   * ★与长图 `useLongImageBuilder` 是同一套语义(跟随 / 熄灭 / 复位),**只有一处故意不同**:
   * 推不出配色时(自由发挥、自定义画风)这边把配色清成 `null`,长图那边保留上一份。
   * 理由:长图 tokens=null 会让 ai 退回 background 三档推色 → 纯矢量贴字(正是本波要消灭的),
   * 那边宁可留着旧的;单帧 tokens=null 只是「配色交给 AI 自己定」——正是「自由发挥」的字面意思。
   * 于是这边多一条不变式:**跟随态下 styleTokens 恒等于当前画风推出来的那份**,
   * 徽标写「跟随画风」时它就真的在跟随;也顺带保证「自由发挥 + 选过配色」有退出路径。
   */
  const [styleTokens, setStyleTokensState] = useState<StyleTokens | null>(null);
  const [followStyle, setFollowStyle] = useState(true);
  // 波B:产品库直选的快照(逐字原文)。★不塞进 prompt —— 那是用户写的字,
  // 覆盖它就是"静默盖掉用户没做完的稿";两者在提交时各占 rawPrompt 的一段。
  const [product, setProduct] = useState<FreeCreateProduct | null>(null);
  const [showProductSpecs, setShowProductSpecs] = useState(false);

  /** 画风 key ← rich tone 反查。★`selectedStyle` 存的是 100+ 字 tone 文本不是 key。 */
  const tokensForTone = useCallback(
    (tone: string | null) => toneToTokens(findStyleByTone(tone)?.key ?? null),
    []
  );

  /** 选画风(L1)。跟随态下同步推一份配色(L2)—— 用户做一个选择,两层都有了。 */
  const setSelectedStyle = useCallback(
    (tone: string | null) => {
      setSelectedStyleState(tone);
      if (!followStyle) return; // 用户已自己选过配色 → L1 不再覆盖 L2
      setStyleTokensState(tokensForTone(tone));
    },
    [followStyle, tokensForTone]
  );

  /** 选配色(L2)。★任一改动都熄灭跟随 —— 之后换画风不再覆盖用户选的配色。 */
  const setStyleTokens = useCallback((next: StyleTokens | null) => {
    setStyleTokensState(next);
    setFollowStyle(false);
  }, []);

  /** 「跟随画风」复位:按当前画风重推(自由发挥 → 回到「AI 自己定」)。 */
  const resetFollowStyle = useCallback(() => {
    setFollowStyle(true);
    setStyleTokensState(tokensForTone(selectedStyle));
  }, [selectedStyle, tokensForTone]);

  /**
   * 归整层**已经吸收**的那一份素材(点 AI 优化那一刻的快照)。
   *
   * ★它决定提交时还要不要追加【产品】【风格】【字体风格】:吸收过就不追加(否则就是
   * 线上那两段互相打架的【风格】),没吸收到就照旧追加(宁可重复也不丢)。
   * 优化失败 / 还原原文 → 置 null,一律退回追加。
   */
  const [absorbed, setAbsorbed] = useState<AbsorbedMaterials | null>(null);

  // Wave 3 (2026-05-17) — AI 优化产物 state
  const [originalPromptSnapshot, setOriginalPromptSnapshot] = useState<string | null>(null);
  const [contentDescription, setContentDescription] = useState("");
  const [systemConstraints, setSystemConstraints] = useState("");
  const [coverageChips, setCoverageChips] = useState<CoverageChip[]>([]);
  const [isAiOptimized, setIsAiOptimized] = useState(false);
  const [optimizeFailureMessage, setOptimizeFailureMessage] = useState<string | null>(null);

  return {
    prompt,
    setPrompt,
    extraConstraint,
    setExtraConstraint,
    ratio,
    setRatio,
    ratioUserTouched,
    setRatioUserTouched,
    ratioSet,
    setRatioSet,
    groupPoints,
    setGroupPoints,
    keepSubjectConsistent,
    setKeepSubjectConsistent,
    groupAnchorRef,
    setGroupAnchorRef,
    groupAnchorOn,
    setGroupAnchorOn,
    groupSubjectSpec,
    setGroupSubjectSpec,
    sceneKey,
    setSceneKey,
    explosive,
    setExplosive,
    mode,
    setMode,
    refImage,
    maskPainted,
    setMaskPainted,
    setRefImage,
    refImages,
    setRefImages,
    logoId,
    setLogoId,
    qrId,
    setQrId,
    useBrandColors,
    setUseBrandColors,
    logoAnchor,
    setLogoAnchor,
    qrAnchor,
    setQrAnchor,
    printContact,
    setPrintContact,
    useGptThinking,
    setUseGptThinking,
    count,
    setCount,
    provider,
    setProvider,
    resolutionTier,
    setResolutionTier,
    scheduleMode,
    setScheduleMode,
    scheduledAt,
    setScheduledAt,
    filter,
    setFilter,
    filterMode,
    setFilterMode,
    polishing,
    setPolishing,
    submitting,
    setSubmitting,
    currentDraftId,
    setCurrentDraftId,
    draftIdParam,
    selectedStyle,
    setSelectedStyle,
    fontStyle,
    setFontStyle,
    styleTokens,
    setStyleTokens,
    followStyle,
    resetFollowStyle,
    product,
    setProduct,
    showProductSpecs,
    setShowProductSpecs,
    originalPromptSnapshot,
    setOriginalPromptSnapshot,
    contentDescription,
    setContentDescription,
    systemConstraints,
    setSystemConstraints,
    coverageChips,
    setCoverageChips,
    isAiOptimized,
    setIsAiOptimized,
    absorbed,
    setAbsorbed,
    optimizeFailureMessage,
    setOptimizeFailureMessage,
  };
}
